import {
  parseMultilineContent,
  stringifyMultilineContent,
  stringifyVariableAsPlaceholder,
} from "@repo/app-types/content/plaintext";
import type { MultilineContent } from "@repo/app-types/content/types";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { useMemo, useState, type FC, type ReactNode } from "react";
import { Can } from "../../../../helpers/teammateAuthorizationContext.js";

export interface RolePlayInstructionsFieldProps {
  label: string;
  description: ReactNode;
  content: MultilineContent;
  onChange: (newContent: MultilineContent) => void;
  height?: string;
}

const ROLE_PLAY_INSTRUCTIONS_MAX_LENGTH = 15000;

export const RolePlayInstructionsField: FC<RolePlayInstructionsFieldProps> = (
  props,
) => {
  const [text, onTextChange] = useMultilineContent(
    props.content,
    props.onChange,
  );

  return (
    <Can
      I={PermissionActions.UPDATE}
      a={PermissionSubjects.JOB_OPENINGS}
      passThrough={true}
    >
      {(allowed) => (
        <Textarea
          label={props.label}
          description={props.description}
          value={text}
          height={props.height ?? "12rem"}
          maxLength={ROLE_PLAY_INSTRUCTIONS_MAX_LENGTH}
          onChange={onTextChange}
          isDisabled={!allowed}
        />
      )}
    </Can>
  );
};

function useMultilineContent(
  content: MultilineContent,
  onContentChange: (newContent: MultilineContent) => void,
) {
  // Once the user changes the text, we'll use that to render the component just in case parsing and re-stringifying is lossy.
  const [changedText, setChangedText] = useState<string | undefined>(undefined);
  const text = useMemo(
    () =>
      changedText ??
      stringifyMultilineContent(content, {
        stringifyVariable: stringifyVariableAsPlaceholder,
      }),
    [changedText, content],
  );

  const onTextChange = (newValue: string) => {
    setChangedText(newValue);
    onContentChange(
      parseMultilineContent(newValue, {
        parsesVariables: false,
      }),
    );
  };

  return [text, onTextChange] as const;
}
